import React                from "react";
import ReactDOM             from "react-dom";
import AppointmentWidget    from "./widgets/AppointmentWidget";
import ClassWrapper         from "./components/ClassWrapper";
import "./reset.css";

// Themes
import amberTheme           from "./themes/AmberTheme";
import greenTheme           from "./themes/GreenTheme";
import nebolitProTheme      from "./themes/NebolitTheme";
import orangeTheme          from "./themes/OrangeTheme";
import pinkTheme            from "./themes/PinkTheme";
import tealTheme            from "./themes/TealTheme";
import cspTheme             from "./themes/CspTheme";
import aldomedTheme         from "./themes/AldomedTheme"
import medikaTheme          from "./themes/MedikaTheme";

export default class WidgetsManager {
    static get widgetConstructors() {
        return {
            "AppointmentWidget" : AppointmentWidget,
        };
    }

    static get staticWidgets() {
        return [
        ];
    }

    static get themes() {
        return {
            "nebolit.pro" : nebolitProTheme, // default theme
            "amber"       : amberTheme,
            "green"       : greenTheme,
            "orange"      : orangeTheme,
            "pink"        : pinkTheme,
            "teal"        : tealTheme,
            "csp"         : cspTheme,
            "medika"      : medikaTheme,
            "aldomed"     : aldomedTheme,
        };
    }

    static getConstructor(widgetName) {
        if (!(widgetName in WidgetsManager.widgetConstructors)) {
            return null;
        }

        return WidgetsManager.widgetConstructors[widgetName];
    }

    static getTheme(themeName) {
        if (!(themeName in WidgetsManager.themes)) {
            return null;
        }

        return WidgetsManager.themes[themeName];
    }

    static setNamespace(namespace) {
        WidgetsManager._namespace = namespace;
    }

    static getNamespace() {
        return WidgetsManager._namespace;
    }

    static setTheme(theme) {
        WidgetsManager._theme = theme;
    }

    static getWidgets() {
        return WidgetsManager._widgets;
    }

    static getWidget(DOMNode) {
        return WidgetsManager._widgets.get(DOMNode);
    }

    static setAllowOverriding(value) {
        WidgetsManager._allowOverriding = value;
    }

    static call(widgetName, methodName, createIfNotExists, DOMNode, props) {
        return new Promise((resolve, reject) => {
            const widgets = WidgetsManager.getWidgets();

            if (!(widgets.has(DOMNode))) {
                if (createIfNotExists === false) {
                    reject(new Error(`Widget ${widgetName} doesn't exists.`));
                }

                WidgetsManager.create(widgetName, DOMNode, props).then((reference) => {
                    reference[methodName]();
                    resolve();
                }).catch((error) => {
                    reject(error);
                });

                return;
            }

            const widget = WidgetsManager.getWidget(DOMNode);
            if (widget.reference === null) {
                widget.pendingActions.push(props);
            } else {
                widget.reference.show();
            }

            resolve();
        });
    }

    static create (widgetName, DOMNode, props, allowOverriding = false, alternateDOMNode = null) {
        return new Promise((resolve, reject) => {
            if (DOMNode === undefined || DOMNode === null) {
                reject(new ReferenceError("DOMNode doesn't exists."));
            }

            if (props === undefined) {
                reject(new ReferenceError("props is required property."));
            }

            if (WidgetsManager._allowOverriding === false && allowOverriding === false && WidgetsManager._widgets.has(DOMNode)) {
                reject(new Error (`Can't mount widget in this node. Widget ${widgetName} alredy exists.`));
            }

            const widget = {
                reference: null,
                pendingActions: [],
            };

            WidgetsManager._widgets.set(DOMNode, widget);
            if (alternateDOMNode !== null) {
                WidgetsManager._widgets.set(alternateDOMNode, widget);
            }

            const widgetConstructor = WidgetsManager.getConstructor(widgetName);
            if (widgetConstructor === null) {
                reject(new Error (`Widget ${widgetName} doesn't exists.`));
            }

            if (props === null) {
                props = {};
            }

            const extendedProps = {
                ...props,
                ref: (reference) => {
                    const widget = WidgetsManager._widgets.get(DOMNode);

                    for (const action of widget.pendingActions) {
                        reference[action]();
                    }

                    widget.reference = reference;
                    widget.pendingActions = [];

                    WidgetsManager._widgets.set(DOMNode, widget);
                    if (alternateDOMNode !== null) {
                        WidgetsManager._widgets.set(alternateDOMNode, widget);
                    }

                    resolve(reference);
                }
            };

            const childrenElement = React.createElement(widgetConstructor, extendedProps);
            const classWrapper = React.createElement(ClassWrapper, {
                namespace: WidgetsManager._namespace,
                theme: WidgetsManager._theme,
            }, childrenElement);

            ReactDOM.render(classWrapper, DOMNode);
        });
    }
}

WidgetsManager._allowOverriding = false;
WidgetsManager._widgets = new Map();
WidgetsManager._namespace = "nebolit-pro";
WidgetsManager._theme = WidgetsManager.getTheme("aldomed");


/** WEBPACK FOOTER **
 ** ./src/widgets-manager.js
 **/